export const SPRITE_VIDEO_LORA_MAX = 2;
export const STANDALONE_VIDEO_LORA_MAX = 3;

const parseRow = (rowEl) => {
  const id = rowEl.querySelector('[data-video-lora-id]')?.value?.trim() || '';
  const strengthRaw = rowEl.querySelector('[data-video-lora-strength]')?.value?.trim() || '';
  return { id, strengthRaw };
};

export const mountVideoLoraRows = ({
  rowsEl,
  addButton,
  titleEl,
  max = STANDALONE_VIDEO_LORA_MAX,
  onChange,
  text,
}) => {
  let cap = max;
  const label = (key, fallback) => (text ? text(key, fallback) : fallback);
  const notify = () => { onChange?.(); };

  const rowCount = () => rowsEl?.querySelectorAll('[data-video-lora-row]')?.length || 0;

  const syncAdd = () => {
    if (!addButton) return;
    const atMax = rowCount() >= cap;
    addButton.disabled = atMax;
    addButton.hidden = atMax;
  };

  const syncTitle = () => {
    if (!titleEl) return;
    const template = label('video_lora_section_title', 'Optional video LoRAs (max {max})');
    titleEl.textContent = template.replaceAll('{max}', String(cap));
  };

  const syncPlaceholders = () => {
    rowsEl?.querySelectorAll('[data-video-lora-id]').forEach((input) => {
      input.placeholder = label('video_lora_id_placeholder', 'civitai version ID (not model id!)');
    });
    rowsEl?.querySelectorAll('[data-video-lora-strength]').forEach((input) => {
      input.placeholder = label('video_lora_strength_placeholder', 'strength (e.g. 1.0)');
    });
    rowsEl?.querySelectorAll('[data-video-lora-remove]').forEach((button) => {
      button.setAttribute('aria-label', label('free_image_remove_lora', 'Remove LoRA'));
    });
    if (addButton) {
      addButton.setAttribute('aria-label', label('free_image_add_lora', 'Add LoRA'));
    }
  };

  const addRow = (entry = null) => {
    if (!rowsEl || rowCount() >= cap) return null;
    const row = document.createElement('div');
    row.className = 'step2-lora-row';
    row.setAttribute('data-video-lora-row', '');

    const idInput = document.createElement('input');
    idInput.type = 'text';
    idInput.className = 'form-input kv-key';
    idInput.setAttribute('data-video-lora-id', '');
    idInput.placeholder = label('video_lora_id_placeholder', 'civitai version ID (not model id!)');
    idInput.setAttribute('aria-label', 'Video LoRA Civitai version ID');
    idInput.tabIndex = 0;
    idInput.maxLength = 500;
    if (entry?.id != null) idInput.value = String(entry.id);

    const strengthInput = document.createElement('input');
    strengthInput.type = 'text';
    strengthInput.className = 'form-input kv-value';
    strengthInput.setAttribute('data-video-lora-strength', '');
    strengthInput.placeholder = label('video_lora_strength_placeholder', 'strength (e.g. 1.0)');
    strengthInput.setAttribute('aria-label', 'Video LoRA strength');
    strengthInput.tabIndex = 0;
    strengthInput.maxLength = 50;
    if (entry?.strength != null && entry.strength !== '') strengthInput.value = String(entry.strength);

    const removeBtn = document.createElement('button');
    removeBtn.type = 'button';
    removeBtn.className = 'btn';
    removeBtn.setAttribute('data-video-lora-remove', '');
    removeBtn.setAttribute('aria-label', label('free_image_remove_lora', 'Remove LoRA'));
    removeBtn.tabIndex = 0;
    removeBtn.innerHTML = '<i class="fas fa-times" aria-hidden="true"></i>';

    const handleRemove = () => {
      row.remove();
      if (rowCount() === 0) addRow();
      syncAdd();
      notify();
    };
    idInput.addEventListener('input', notify);
    strengthInput.addEventListener('input', notify);
    removeBtn.addEventListener('click', handleRemove);
    removeBtn.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      event.preventDefault();
      handleRemove();
    });

    row.append(idInput, strengthInput, removeBtn);
    rowsEl.appendChild(row);
    syncAdd();
    return row;
  };

  const collect = ({ strict = false } = {}) => {
    if (!rowsEl) return null;
    const rows = Array.from(rowsEl.querySelectorAll('[data-video-lora-row]'));
    const entries = [];
    for (const row of rows) {
      const { id, strengthRaw } = parseRow(row);
      if (!id && !strengthRaw) continue;
      if (!id) {
        if (strict) throw new Error(label('video_lora_id_required', 'Please provide a LoRA ID.'));
        continue;
      }
      const strength = strengthRaw ? Number.parseFloat(strengthRaw) : NaN;
      if (strengthRaw && !Number.isFinite(strength)) {
        if (strict) throw new Error(label('video_lora_strength_invalid', 'LoRA strength must be a number.'));
        continue;
      }
      entries.push({
        id,
        source: 'civitai',
        ...(Number.isFinite(strength) ? { strength } : {}),
      });
    }
    if (strict && entries.length > cap) {
      const template = label('video_lora_limit', 'Remove a LoRA before creating a video (max {max}).');
      throw new Error(template.replaceAll('{max}', String(cap)));
    }
    return entries.length ? entries : null;
  };

  const filledCount = () => {
    const payload = collect();
    return Array.isArray(payload) ? payload.length : 0;
  };

  const setMax = (next) => {
    cap = next;
    while (rowsEl && rowCount() > cap) {
      const empty = Array.from(rowsEl.querySelectorAll('[data-video-lora-row]')).find((row) => {
        const { id, strengthRaw } = parseRow(row);
        return !id && !strengthRaw;
      });
      if (!empty) break;
      empty.remove();
    }
    if (rowCount() === 0) addRow();
    syncTitle();
    syncAdd();
    notify();
  };

  const reset = () => {
    if (rowsEl) rowsEl.replaceChildren();
    addRow();
    syncTitle();
    notify();
  };

  const setEntries = (loras) => {
    const list = Array.isArray(loras) ? loras : (loras ? [loras] : []);
    if (rowsEl) rowsEl.replaceChildren();
    const trimmed = list.filter((entry) => entry && entry.id != null).slice(0, cap);
    if (!trimmed.length) addRow();
    else trimmed.forEach((entry) => addRow(entry));
    syncTitle();
    notify();
  };

  addButton?.addEventListener('click', () => {
    addRow();
    notify();
  });
  addRow();
  syncTitle();
  document.addEventListener('languageChanged', () => {
    syncTitle();
    syncPlaceholders();
  });

  return {
    collect,
    filledCount,
    setMax,
    reset,
    setEntries,
    overLimit: () => filledCount() > cap,
    limitMessage: () => label('video_lora_limit', 'Remove a LoRA before creating a video (max {max}).').replaceAll('{max}', String(cap)),
  };
};
